import React, { useMemo } from 'react'
import { EditCartItemProps, SipValidationType, StpValidationType } from '../types'
import { Box, Grid, Skeleton, Theme, Typography, useTheme } from '@mui/material'
import { Formik, useFormik } from '../../../lib/formik/Formik';
import { StpScheme } from './Scheme';
import dayjs from '../../../utils/dayjs';
import { InvestmentFundType } from '../../../redux-store/types/api-types';
import { MpowerValidationType } from '../../investorOverview/types';
import { useSelector } from 'react-redux';
import { RootStateType } from '../../../redux-store/reducers';
import { CustomeBlueDatePicker, CustomeBlueDropDown, CustomeModalButton, CustomeNumberInput } from '../../investorOverview/transcation/FromInput';
import { STP_FIRST_ORDER_FLAG_LABEL, WEEKLYDAYS } from '../../../utils/constant';
import { StpWeekDaysObj } from '../../../redux-store/types/funds';
import MFCheckbox from '../../../lib/formik/Checkbox';
import { InfoIconOutlined } from '../../../assets/SvgIcons';
import { getDailyFreqInstallmentsMsg } from '../../../utils/utilityFunctions';

type Props = {
    data: EditCartItemProps
    currentTransactionType: string
    validation: StpValidationType
    scheme: InvestmentFundType
    schemeLoading: boolean
    weeklyDaysValidation: Array<StpWeekDaysObj>
}

export const handleStpDateValidation = ({
    ArrayOfDate,
    date,
    weekDaysAllowed,
    frequency,
}: {
    ArrayOfDate: Array<string | number>;
    date: dayjs.Dayjs;
    weekDaysAllowed: Array<string>;
    frequency: string;
}): boolean => {
    if (frequency === "WEEKLY") {
        if (!(weekDaysAllowed ?? []).length) return false

        const capitalizedDays = weekDaysAllowed.map(day =>
            day.toString().charAt(0) + day.toString().slice(1).toLowerCase()
        );

        const day = capitalizedDays.map(item => WEEKLYDAYS.indexOf(item))

        return !day?.includes(dayjs(date).day());
    }

    if (ArrayOfDate.length === 0) {
        return false
    }
    if (typeof ArrayOfDate[0] === 'number') {
        return !ArrayOfDate?.includes(dayjs(date).date());
    }

    if (typeof ArrayOfDate[0] === 'string') {
        const capitalizedDays = ArrayOfDate.map(day =>
            day.toString().charAt(0) + day.toString().slice(1).toLowerCase()
        );
        const day = capitalizedDays.map(item => WEEKLYDAYS.indexOf(item))
        return !day?.includes(dayjs(date).day());
    }
    return false
}


export default function Stp(props: Props) {
    const theme = useTheme()

    const { data, currentTransactionType, validation, scheme, schemeLoading, weeklyDaysValidation } = props
    const { onSave, stpAmount, frequency, onCancel, noOfInstallments, stpDate, mandate, endDate, isLoading, firstOrder } = data

    const { locale, currencyCode, currencySymbol } = useSelector((store: RootStateType) => store.currencyInfo);

    // sheme
    const schemeValidation = StpScheme({ stpAmount, frequency, noOfInstallments, stpDate, scheme })
    const frequencyArray = scheme?.frequencies?.filter(item => item.frequency && item.tranasactionType === 'STP')



    // handleSubmit
    function handleSubmitBtn(formValues: any) {
        formValues.stpDate = dayjs(formValues.stpDate).format('DD/MM/YYYY')
        onSave(
            data.id,
            currentTransactionType,
            {
                amount: formValues.stpAmount,
                frequency: formValues.frequency,
                noOfInstallment: formValues.frequency === 'DAILY' ? 0 : formValues.noOfInstallments,
                // sipDate: dayjs(formValues.stpDate).format('DD/MM/YYYY'),
                sipDate: formValues.stpDate,
                endDate: formValues.frequency === 'DAILY' ? dayjs(formValues.stpEndDate).format('DD/MM/YYYY') : '',
                // mandateId: formValues.mandates
                firstOrderFlag: formValues.firstOrder ? "Y" : "N",
            }
        )
    }

    const Daily = scheme.validations?.filter((item) => item?.frequency === 'DAILY' && item?.transactionType === 'STP')?.[0]
    const Weekly = scheme.validations?.filter((item) => item?.frequency === 'WEEKLY' && item?.transactionType === 'STP')?.[0]
    const WeeklyDays = scheme.validations?.filter((item) => item?.frequency === 'WEEKLYDAYS' && item?.transactionType === 'STP')?.[0]
    const Monthly = scheme.validations?.filter((item) => item?.frequency === 'MONTHLY' && item?.transactionType === 'STP')?.[0]
    const Quarterly = scheme.validations?.filter((item) => item?.frequency === 'QUARTERLY' && item?.transactionType === 'STP')?.[0]
    const Yearly = scheme.validations?.filter((item) => item?.frequency === 'YEARLY' && item?.transactionType === 'STP')?.[0]

    const stpMinAmount = (key = 'minimumInstallmentAmount' as keyof MpowerValidationType, frequency: string) => {

        if (frequency === 'WEEKLY') {
            return Weekly?.[key]
        } else if (frequency === 'MONTHLY') {
            return Monthly?.[key]
        } else if (frequency === 'QUARTERLY') {
            return Quarterly?.[key]
        } else if (frequency === 'YEARLY') {
            return Yearly?.[key]
        } else if (frequency === 'DAILY') {
            return Daily?.[key]
        } else if (frequency === 'WEEKLYDAYS') {
            return WeeklyDays?.[key]
        } else {
            return 0
        }
    }

    return (
        <Formik
            enableReinitialize
            initialValues={{
                stpAmount: stpAmount || '0',
                frequency: frequency || '',
                noOfInstallments: noOfInstallments || '',
                stpDate: dayjs(stpDate, 'DD/MM/YYYY') || '',
                mandates: mandate || '',
                stpEndDate: endDate ? dayjs(endDate, 'DD/MM/YYYY') : '',
                firstOrder: firstOrder,
                weeklyDaysValidation: (
                    (weeklyDaysValidation as Array<StpWeekDaysObj>) ?? []
                )?.find(obj =>
                    `${obj?.companyId ?? ""}` ===
                    `${(scheme as InvestmentFundType)?.fundAmcId ?? ""}`
                ) ?? {} as StpWeekDaysObj
            }}
            validationSchema={schemeValidation}
            onSubmit={handleSubmitBtn}>
            {(formik) => {
                return (
                    <Box component="form" onSubmit={formik.handleSubmit}>
                        <Grid container spacing={2}>
                            <Grid item lg={6} md={6} sm={12} xs={12}>
                                <CustomeNumberInput
                                    name={`stpAmount`}
                                    isLoading={schemeLoading}
                                    disable={isLoading}
                                    textType="number"
                                    label='STP Amount'
                                    minlabel={`Min: ${String.fromCharCode(parseInt(currencySymbol, 16))} ${stpMinAmount('minimumInstallmentAmount', formik.values.frequency)}`}
                                    removeIcons={true}
                                    warningText={'amount'}
                                />
                            </Grid>
                            <Grid item lg={6} md={6} sm={12} xs={12}>
                                <CustomeBlueDropDown
                                    name={`frequency`}
                                    isLoading={schemeLoading}
                                    disabled={isLoading}
                                    label='Frequency'
                                    list={frequencyArray?.map(item => ({ value: item.frequency, label: item.frequency })) as any}
                                />
                            </Grid>
                            {formik.values.frequency !== 'DAILY' && (<Grid item lg={6} md={6} sm={12} xs={12}>
                                <CustomeNumberInput
                                    textType="number"
                                    isLoading={schemeLoading}
                                    disable={isLoading}
                                    label='No. of Installments'
                                    name={`noOfInstallments`}
                                    minlabel={`Min: ${formik.values.frequency === 'DAILY' ? '0' : stpMinAmount('minimumInstallments', formik.values.frequency)}`}
                                    minValue={formik.values.frequency === 'DAILY' ? 0 : validation?.installment?.minValue}
                                    removeIcons={true}
                                />
                            </Grid>)}
                            <Grid item lg={6} md={6} sm={12} xs={12}>
                                <CustomeBlueDatePicker
                                    name={`stpDate`}
                                    isLoading={schemeLoading}
                                    disabled={isLoading}
                                    disablePast={true}
                                    label={formik.values.frequency === 'DAILY' ? "STP Start Date" : "STP Date"}
                                    shouldDisableDate={(date: dayjs.Dayjs) => handleStpDateValidation({
                                        ArrayOfDate: stpMinAmount('investmentDates', formik.values.frequency) || [],
                                        date,
                                        weekDaysAllowed: formik.values?.weeklyDaysValidation?.STP_WEEKDAYS ?? [],
                                        frequency: formik.values.frequency,
                                    })}
                                    // disableDateList={stpMinAmount('investmentDates')}
                                    // For T+1 date
                                    minDate={dayjs().add(1, 'day')}
                                />
                            </Grid>
                            {formik.values.frequency === 'DAILY' && (
                                <Grid item lg={6} md={6} sm={12} xs={12}>
                                    <CustomeBlueDatePicker
                                        name='stpEndDate'
                                        isLoading={schemeLoading}
                                        disabled={isLoading}
                                        disablePast
                                        label="STP End Date"
                                        shouldDisableDate={(date: dayjs.Dayjs) => dayjs(date).isBefore(dayjs(formik.values.stpDate))}
                                    />
                                </Grid>
                            )}
                            {(!schemeLoading && (formik.values.frequency === 'DAILY')) && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        columnGap: "5px",
                                        marginLeft: "16px",
                                        marginTop: "10px"
                                    }}>
                                    <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center" }}>
                                        <InfoIconOutlined />
                                    </Box>
                                    <Typography
                                        variant='subtitle_600'
                                        sx={(theme: Theme) => ({
                                            color: theme.palette.grey[500]
                                        })}
                                    >
                                        {getDailyFreqInstallmentsMsg(Daily?.minimumInstallments ?? 0)}
                                    </Typography>
                                </Box>
                            )}
                            <Grid item xs={12} md={6} mt={5}>
                                {schemeLoading && (
                                    <Skeleton
                                        variant="rectangular"
                                        width='100%'
                                        height={50}
                                        sx={{ borderRadius: 2 }}
                                    />
                                )}
                                {!schemeLoading && (
                                    <Box
                                        sx={{
                                            width: '100%',
                                            padding: '4px 0px 4px 15px',
                                            backgroundColor: theme.palette.primary[50],
                                            borderRadius: 2
                                        }}
                                    >
                                        <MFCheckbox
                                            name="firstOrder"
                                            label={STP_FIRST_ORDER_FLAG_LABEL}
                                            disabled
                                        />
                                    </Box>
                                )}
                            </Grid>
                            <Grid
                                item
                                container
                                xs={12}
                                mt={2}
                                sx={{
                                    justifyContent: "center",
                                    alignItems: "center",
                                    borderTop: `0.5px solid ${theme.palette.text.secondary}B2`,
                                    paddingTop: "20px"
                                }}
                            >
                                <Grid
                                    item
                                    container
                                    xs={10}
                                    sx={{
                                        columnGap: "35px",
                                        flexWrap: "nowrap",
                                        paddingTop: "30px",
                                    }}
                                >
                                    <Grid
                                        item
                                        xs={6}
                                    >
                                        <CustomeModalButton
                                            disabled={isLoading || schemeLoading}
                                            onClick={() => onCancel()}
                                            variant='outlined'
                                            type='button'
                                            btnText='Cancel'
                                            boxStyle={{
                                                color: theme.palette.text.primary,
                                            }}
                                        />
                                    </Grid>
                                    <Grid
                                        item
                                        xs={6}
                                    >
                                        <CustomeModalButton
                                            type='submit'
                                            disabled={!formik.dirty || schemeLoading}
                                            isLoading={isLoading}
                                            variant='contained'
                                            btnText='Save Changes'
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>

                    </Box>
                )
            }}

        </Formik>
    )
}